/**
 * Downloads a file from a URL, handling CORS issues with S3 URLs
 * Works for any file type - images, PDFs, documents, etc.
 * Always downloads directly without opening in new window
 */
export async function downloadS3File(url, fileName, options = { neverOpenNewWindow: true }) {
  if (!url) {
    console.error("No URL provided");
    return;
  }

  const urlLower = String(url).toLowerCase();
  const isImage = urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i);
  const isRasterImage = urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i); // Exclude SVG as it needs different handling
  const fileExtension = urlLower.match(/\.([^.]+)$/)?.[1] || 'bin';
  
  // Helper function to trigger download from blob
  const downloadBlob = (blob, name) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = name || `file.${fileExtension}`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Clean up after a delay to ensure download starts
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
  };

  // Normalize URL
  const fullUrl = url.startsWith('http') ? url : (url.startsWith('/') ? `${window.location.origin}${url}` : url);
  
  // Check if URL is cross-origin
  const isCrossOrigin = (() => {
    try {
      if (!fullUrl || !fullUrl.startsWith('http')) return false;
      const u = new URL(fullUrl);
      return u.origin !== window.location.origin;
    } catch (_) {
      return true;
    }
  })();

  try {
    // Method 1: Try fetch with CORS (works if CORS is configured on S3)
    try {
      const response = await fetch(fullUrl, { 
        mode: 'cors', 
        credentials: 'omit',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        downloadBlob(blob, fileName || `file.${fileExtension}`);
        return;
      }
    } catch (fetchError) {
      console.log("Fetch with CORS failed, trying alternative methods:", fetchError);
    }

    // Method 2: For images, try XMLHttpRequest (sometimes works when fetch doesn't)
    if (isImage) {
      try {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', fullUrl, true);
          xhr.responseType = 'blob';
          xhr.onload = () => {
            if (xhr.status === 200) {
              resolve(xhr.response);
            } else {
              reject(new Error(`XHR failed with status ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.send();
        });
        
        downloadBlob(blob, fileName || `file.${fileExtension}`);
        return;
      } catch (xhrError) {
        console.log("XHR method failed, trying canvas method:", xhrError);
      }

      // Method 3: For raster images only, try canvas method (SVG can't be drawn to canvas easily)
      if (isRasterImage) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error('Image load failed'));
            // Add timestamp to avoid cache issues
            img.src = fullUrl + (fullUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
          });

          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          const mimeType = `image/${fileExtension === 'jpg' || fileExtension === 'jpeg' ? 'jpeg' : fileExtension === 'png' ? 'png' : fileExtension === 'gif' ? 'gif' : fileExtension === 'webp' ? 'webp' : 'png'}`;
          
          // Wait for canvas toBlob to complete
          await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
              if (blob) {
                downloadBlob(blob, fileName || `file.${fileExtension}`);
                resolve();
              } else {
                reject(new Error('Canvas toBlob failed'));
              }
            }, mimeType);
          });
          return;
        } catch (canvasError) {
          console.log("Canvas method failed:", canvasError);
        }
      }
    }

    // Method 4: Try XMLHttpRequest for all file types (non-images)
    if (!isImage) {
      try {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', fullUrl, true);
          xhr.responseType = 'blob';
          xhr.onload = () => {
            if (xhr.status === 200) {
              resolve(xhr.response);
            } else {
              reject(new Error(`XHR failed with status ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.send();
        });
        
        downloadBlob(blob, fileName || `file.${fileExtension}`);
        return;
      } catch (xhrError) {
        console.log("XHR method failed for non-image:", xhrError);
      }
    }

    // Method 5: Last resort - only for same-origin URLs (download attribute works)
    // For cross-origin, we must use blob URLs, so if we reach here, all blob methods failed
    if (!isCrossOrigin) {
      try {
        const link = document.createElement("a");
        link.href = fullUrl;
        link.download = fileName || `file.${fileExtension}`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } catch (finalError) {
        console.error("Same-origin download failed:", finalError);
      }
    }
    
    // If we reach here, all methods failed for cross-origin resource
    console.error("All download methods failed for cross-origin resource. CORS may not be configured on the server.");
    throw new Error("Download failed: All methods exhausted. The file server may not allow cross-origin downloads.");
  } catch (err) {
    console.error("Download error:", err);
    throw err;
  }
}

